import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { URLS } from '../../data/navigation/navigation.data';
import { Product, ProductLine } from '../../model/product/product.model';

@Injectable( {
  providedIn: 'root'
} )
export class ProductService {

  URLS = URLS;

  public product: Observable<Product>;
  private productSubject: BehaviorSubject<Product>;

  constructor( private router: Router ) {
    let productStorageItem: Product = JSON.parse( localStorage.getItem( 'product' ) ?
      localStorage.getItem( 'product' ) : sessionStorage.getItem( 'product' ) );
    productStorageItem = productStorageItem ? productStorageItem : undefined;

    this.productSubject = new BehaviorSubject<Product>( productStorageItem );
    this.product = this.productSubject.asObservable();
  }

  setSelectedProduct( product: Product ) {
    if ( product ) {
      if ( localStorage.getItem( 'currentUser' ) ) {
        sessionStorage.removeItem( 'product' );
        localStorage.setItem( 'product', JSON.stringify( product ) );
      } else {
        sessionStorage.setItem( 'product', JSON.stringify( product ) );
      }
      this.productSubject.next( product );
    } else {
      localStorage.removeItem( 'product' );
      sessionStorage.removeItem( 'product' );
    }
  }

  /**
   * The real destination a product link points at, mirrors the navigation
   * branches in `redirectToProductUrl` so it can be bound to a crawlable
   * `href` on the anchor (SEO: links must expose a valid href).
   */
  productUrl( product: Product, productLine?: ProductLine ): string {
    if ( product.redirectToUrl ) {
      return product.url;
    }
    if ( product.redirectToUrlAlter && product.urlAlter ) {
      return product.urlAlter;
    }
    const base = product.url || URLS.root;
    if ( base === URLS.maintenance ) {
      return base;
    }
    const fragment = ( productLine?.key ?? '' ) + '-' + product.key;
    return base + '#' + fragment;
  }

  redirectToProductUrl( product: Product, productLine?: ProductLine ) {
    const fragment = productLine?.key + '-' + product.key;

    if ( product.redirectToUrl ) {
      window.open( product.url, '_blank' );
    } else if ( product.redirectToUrlAlter ) {
      window.open( product.urlAlter, '_blank' );
    } else if ( product.url === URLS.maintenance ) {
      this.setSelectedProduct( undefined );
      this.router.navigate( [ URLS.maintenance ] ).then( () => {
        setTimeout( () => {
          let element = document.getElementById( 'maintenance-head' );
          if ( element != undefined ) {
            element.scrollIntoView( { behavior: 'smooth', block: 'start' } );
          } else {
            this.router.navigate( [ product.url ] );
          }
        }, 100 );
      } );
    } else {
      this.router.navigate( [ product.url ], { fragment } ).then( () => {
        setTimeout( () => {
          const element = document.getElementById( fragment );
          if ( element != undefined ) {
            element.scrollIntoView( { behavior: 'smooth', block: 'start' } );
            return;
          }
          // No element with this fragment id on the destination (e.g. a tool or app page
          // has no home-page product anchor): the page opens at its top (owner 2026-10-12:
          // a footer link left the reader at the new page's foot). Do NOT re-navigate to
          // product.url here: on the tool pages that re-fires the bare-URL random
          // generation, causing a duplicate random + double UI flash. The app scrolls on
          // <body>, not the window, so both are reset.
          document.body.scrollTop = 0;
          document.documentElement.scrollTop = 0;
          window.scrollTo( 0, 0 );
        }, 100 );
      } );
    }
  }
}
